import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  Radio, Users, Eye, Play, Plus, Calendar,
  Video, Flame, Clock, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LiveListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token, user } = useAuth();
  
  // State
  const [liveStreams, setLiveStreams] = useState([]);
  const [scheduledStreams, setScheduledStreams] = useState([]);
  const [replays, setReplays] = useState([]);
  const [myStreams, setMyStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newStreamTitle, setNewStreamTitle] = useState('');
  const [newStreamDescription, setNewStreamDescription] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Fetch data
  const fetchStreams = useCallback(async () => {
    try {
      const [liveRes, scheduledRes, replaysRes] = await Promise.all([
        fetch(`${API_URL}/api/streams/live`),
        fetch(`${API_URL}/api/streams/scheduled`),
        fetch(`${API_URL}/api/streams/replays`)
      ]);
      
      if (liveRes.ok) {
        const data = await liveRes.json();
        setLiveStreams(data.streams || []);
      }
      
      if (scheduledRes.ok) {
        const data = await scheduledRes.json();
        setScheduledStreams(data.streams || []);
      }
      
      if (replaysRes.ok) {
        const data = await replaysRes.json();
        setReplays(data.streams || []);
      }
    } catch (err) {
      console.error('Failed to fetch streams:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchMyStreams = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/my-streams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setMyStreams(data.streams || []);
      }
    } catch (err) {
      console.error('Failed to fetch my streams:', err);
    }
  }, [token]);
  
  useEffect(() => {
    fetchStreams();
    fetchMyStreams();
    
    // Poll for live streams
    const interval = setInterval(fetchStreams, 10000);
    return () => clearInterval(interval);
  }, [fetchStreams, fetchMyStreams]);
  
  // Create stream
  const handleCreateStream = async () => {
    if (!newStreamTitle.trim()) return;
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/streams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newStreamTitle,
          description: newStreamDescription
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setShowCreateModal(false);
        setNewStreamTitle('');
        setNewStreamDescription('');
        fetchMyStreams();
        
        // Navigate to stream broadcast page
        navigate(`/live/${data.stream.id}/broadcast`);
      }
    } catch (err) {
      console.error('Failed to create stream:', err);
    } finally {
      setCreating(false);
    }
  };
  
  // Start stream
  const handleStartStream = async (streamId) => {
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        navigate(`/live/${streamId}`);
      }
    } catch (err) {
      console.error('Failed to start stream:', err);
    }
  };
  
  // Stream card component
  const StreamCard = ({ stream, showStatus = true }) => {
    const isLive = stream.status === 'live';
    
    return (
      <div
        onClick={() => navigate(`/live/${stream.id}`)}
        className="group cursor-pointer bg-gray-900 rounded-xl overflow-hidden hover:ring-2 hover:ring-orange-500/50 transition-all"
        data-testid={`stream-card-${stream.id}`}
      >
        {/* Thumbnail */}
        <div className="relative aspect-video bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
          {stream.thumbnail_url ? (
            <img 
              src={stream.thumbnail_url} 
              alt={stream.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <Radio className={`h-12 w-12 ${isLive ? 'text-red-500 animate-pulse' : 'text-gray-600'}`} />
          )}
          
          {/* Status badges */}
          {showStatus && (
            <div className="absolute top-2 left-2 flex gap-2">
              {isLive ? (
                <Badge className="bg-red-500 text-white flex items-center gap-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  {t('live.live', 'LIVE')}
                </Badge>
              ) : stream.status === 'scheduled' ? (
                <Badge className="bg-blue-500 text-white">
                  <Calendar className="h-3 w-3 mr-1" />
                  {t('live.scheduledBadge', 'Scheduled')}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-gray-300 border-gray-600">
                  <Play className="h-3 w-3 mr-1" />
                  {t('live.tabReplays', 'Replays')}
                </Badge>
              )}
            </div>
          )}
          
          {/* Stats */}
          <div className="absolute bottom-2 right-2 flex gap-2">
            {isLive && (
              <Badge variant="outline" className="bg-black/60 text-white border-0">
                <Eye className="h-3 w-3 mr-1" />
                {stream.viewer_count}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Info */}
        <div className="p-3">
          <div className="flex items-start gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={stream.creator?.profile_picture} />
              <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-xs">
                {stream.creator?.display_name?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-white truncate group-hover:text-orange-400 transition-colors">
                {stream.title}
              </h3>
              <p className="text-sm text-gray-400 truncate">
                {stream.creator?.display_name}
              </p>
              {stream.status === 'ended' && (
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                  <Clock className="h-3 w-3" />
                  {Math.floor(stream.duration_seconds / 60)} min
                  {stream.total_views > 0 && (
                    <>
                      <span className="mx-1">•</span>
                      <Eye className="h-3 w-3" />
                      {stream.total_views} {t('live.views', 'views')}
                    </>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // My stream card with actions
  const MyStreamCard = ({ stream }) => {
    const isLive = stream.status === 'live';
    const isScheduled = stream.status === 'scheduled';

    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-gray-900 rounded-xl">
        <div className="w-full sm:w-32 aspect-video bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
          <Radio className={`h-8 w-8 ${isLive ? 'text-red-500 animate-pulse' : 'text-gray-600'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {isLive && (
              <Badge className="bg-red-500 text-white text-xs">{t('live.live', 'LIVE')}</Badge>
            )}
            {isScheduled && (
              <Badge className="bg-blue-500 text-white text-xs">{t('live.ready', 'Ready')}</Badge>
            )}
            <h3 className="font-medium text-white truncate">{stream.title}</h3>
          </div>
          {isLive && (
            <p className="text-sm text-gray-400">
              <Eye className="h-3 w-3 inline mr-1" />
              {stream.viewer_count} {t('live.viewers', 'viewers')}
            </p>
          )}
        </div>

        <div className="flex gap-2 flex-shrink-0">
          {isScheduled && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/live/${stream.id}/broadcast`);
              }}
              className="flex-1 sm:flex-none bg-gradient-to-r from-orange-500 to-red-500"
              data-testid="go-broadcast-btn"
            >
              <Video className="h-4 w-4 mr-2" />
              {t('live.studio', 'Studio')}
            </Button>
          )}
          {isLive && (
            <>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/live/${stream.id}/broadcast`);
                }}
                className="flex-1 sm:flex-none bg-gradient-to-r from-orange-500 to-red-500"
              >
                <Video className="h-4 w-4 mr-2" />
                {t('live.studio', 'Studio')}
              </Button>
              <Button
                onClick={() => navigate(`/live/${stream.id}`)}
                variant="outline"
                className="flex-1 sm:flex-none border-gray-600 text-gray-300"
              >
                {t('live.preview', 'Preview')}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-950 text-white px-4 py-4 md:p-6" data-testid="live-list-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-3xl font-bold flex items-center gap-2">
              <Radio className="h-5 w-5 md:h-8 md:w-8 text-red-500 flex-shrink-0" />
              {t('live.title', 'Live Streaming')}
            </h1>
            <p className="text-gray-400 mt-1 text-sm md:text-base">
              {t('live.subtitle', 'Watch live cooking classes')}
            </p>
          </div>

          {token && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex-shrink-0 bg-gradient-to-r from-orange-500 to-red-500 text-sm px-3"
              data-testid="create-stream-btn"
            >
              <Plus className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">{t('live.createLive', 'Create a Live')}</span>
              <span className="sm:hidden">Live</span>
            </Button>
          )}
        </div>
        
        {/* My Streams (if has any active) */}
        {myStreams.filter(s => ['live', 'scheduled'].includes(s.status)).length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Video className="h-5 w-5 text-orange-500" />
              {t('live.myStreams', 'My Streams')}
            </h2>
            <div className="space-y-3">
              {myStreams
                .filter(s => ['live', 'scheduled'].includes(s.status))
                .map(stream => (
                  <MyStreamCard key={stream.id} stream={stream} />
                ))}
            </div>
          </div>
        )}
        
        {/* Tabs */}
        <Tabs defaultValue="live" className="w-full">
          <TabsList className="w-full bg-gray-900 border border-gray-800">
            <TabsTrigger value="live" className="flex-1 data-[state=active]:bg-red-500/20 data-[state=active]:text-red-500">
              <Radio className="h-4 w-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">{t('live.tabLive', 'Live')}</span>
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="flex-1 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-500">
              <Calendar className="h-4 w-4 mr-1.5 flex-shrink-0" />
              <span className="truncate hidden sm:inline">{t('live.tabScheduled', 'Scheduled')}</span>
              <span className="truncate sm:hidden">{t('live.tabScheduledShort', 'Sched.')}</span>
            </TabsTrigger>
            <TabsTrigger value="replays" className="flex-1 data-[state=active]:bg-gray-500/20 data-[state=active]:text-gray-300">
              <Play className="h-4 w-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">{t('live.tabReplays', 'Replays')}</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="live" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full" />
              </div>
            ) : liveStreams.length === 0 ? (
              <div className="text-center py-12">
                <Radio className="h-16 w-16 mx-auto text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-400">{t('live.noLive', 'No live streams')}</h3>
                <p className="text-gray-500 mt-2">{t('live.noLiveDesc', 'Check back later or start your own live!')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {liveStreams.map(stream => (
                  <StreamCard key={stream.id} stream={stream} />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="scheduled" className="mt-6">
            {scheduledStreams.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-16 w-16 mx-auto text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-400">{t('live.noScheduled', 'No scheduled streams')}</h3>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {scheduledStreams.map(stream => (
                  <StreamCard key={stream.id} stream={stream} />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="replays" className="mt-6">
            {replays.length === 0 ? (
              <div className="text-center py-12">
                <Play className="h-16 w-16 mx-auto text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-400">{t('live.noReplays', 'No replays available')}</h3>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {replays.map(stream => (
                  <StreamCard key={stream.id} stream={stream} showStatus={false} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Create Stream Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radio className="h-5 w-5 text-red-500" />
              {t('live.createLive', 'Create a Live')}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('live.sharePassion', 'Share your culinary passion live!')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder={t('live.streamTitle', 'Stream title')}
              value={newStreamTitle}
              onChange={(e) => setNewStreamTitle(e.target.value)}
              className="bg-gray-800 border-gray-700"
            />
            <Input
              placeholder={t('live.descriptionOptional', 'Description (optional)')}
              value={newStreamDescription}
              onChange={(e) => setNewStreamDescription(e.target.value)}
              className="bg-gray-800 border-gray-700"
            />
          </div>
          
          <DialogFooter>
            <Button
              onClick={handleCreateStream}
              disabled={!newStreamTitle.trim() || creating}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500"
            >
              {creating ? t('live.creating', 'Creating...') : t('live.createAndStart', 'Create and start')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
